import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ear, Megaphone, PhoneOff, Users } from 'lucide-react';
import { PageHeader, Row, Select } from '../components/form';
import { Alert, Badge, Button, Card, Input, cn } from '../components/ui';
import { ApiError, api, type Extension } from '../lib/api';

interface LiveCall {
  callId: string;
  channelId: string;
  from: string;
  fromName: string;
  to: string;
  startedAt: string;
  at: string;
  monitors: number;
}

interface MonitorSession {
  id: string;
  mode: 'listen' | 'whisper' | 'barge';
  targetChannelId: string;
  startedAt: number;
}

const MODES = [
  {
    value: 'listen' as const,
    label: 'Listen',
    icon: Ear,
    hint: 'Hear the call. Neither party can hear you.',
  },
  {
    value: 'whisper' as const,
    label: 'Whisper',
    icon: Megaphone,
    hint: 'Coach the agent. The caller cannot hear you.',
  },
  {
    value: 'barge' as const,
    label: 'Barge',
    icon: Users,
    hint: 'Join the call. Everyone hears everyone.',
  },
];

/**
 * The operator panel.
 *
 * Live calls come from the engine's in-memory map, not from `call_records` — a supervisor needs
 * the channel id to monitor, and a projection is always a moment behind, so a call that looks
 * live in a projection may already have ended.
 */
export function ActiveCalls() {
  const queryClient = useQueryClient();
  const [monitoring, setMonitoring] = useState<string | null>(null);

  const { data: calls = [] } = useQuery({
    queryKey: ['active-calls'],
    queryFn: () => api.get<LiveCall[]>('/api/v1/calls/active'),
    // Two seconds: fast enough that the panel feels live, slow enough that a browser left open
    // all day is not a meaningful load on the engine.
    refetchInterval: 2_000,
  });

  const { data: sessions = [] } = useQuery({
    queryKey: ['monitor-sessions'],
    queryFn: () => api.get<MonitorSession[]>('/api/v1/monitor'),
    refetchInterval: 3_000,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Active calls"
        description="Everything in progress right now, and who is listening to it."
      />

      {sessions.length > 0 && (
        <MonitorBar
          sessions={sessions}
          onChanged={() => void queryClient.invalidateQueries({ queryKey: ['monitor-sessions'] })}
        />
      )}

      {calls.length === 0 ? (
        <Card>
          <p className="py-8 text-center text-sm text-muted-foreground">No calls in progress.</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {calls.map((call) => (
            <Card key={call.callId} className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {call.fromName || call.from}
                    <span className="mx-2 text-muted-foreground">→</span>
                    {call.to || '—'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <Elapsed since={call.startedAt} /> · in {call.at.replace(/_/g, ' ')}
                    {call.monitors > 0 && (
                      <>
                        {' · '}
                        <span className="text-destructive">{call.monitors} listening</span>
                      </>
                    )}
                  </p>
                </div>

                <Button
                  variant="secondary"
                  onClick={() =>
                    setMonitoring(monitoring === call.channelId ? null : call.channelId)
                  }
                >
                  <Ear className="mr-1.5 size-4" />
                  Monitor
                </Button>
              </div>

              {monitoring === call.channelId && (
                <StartMonitor
                  channelId={call.channelId}
                  onStarted={() => {
                    setMonitoring(null);
                    void queryClient.invalidateQueries({ queryKey: ['monitor-sessions'] });
                  }}
                />
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StartMonitor({ channelId, onStarted }: { channelId: string; onStarted: () => void }) {
  const [extension, setExtension] = useState('');
  const [mode, setMode] = useState<'listen' | 'whisper' | 'barge'>('listen');

  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () => api.get<Extension[]>('/api/v1/extensions'),
  });

  useEffect(() => {
    if (!extension && extensions[0]) setExtension(extensions[0].number);
  }, [extensions, extension]);

  const start = useMutation({
    mutationFn: () =>
      api.post('/api/v1/monitor', { channelId, supervisorExtension: extension, mode }),
    onSuccess: onStarted,
  });

  const chosen = MODES.find((m) => m.value === mode)!;

  return (
    <div className="space-y-3 rounded-[--radius] border border-border bg-muted/40 p-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Row label="Your extension" hint="This phone rings first, then joins the call.">
          <Select value={extension} onChange={setExtension}>
            {extensions.map((e) => (
              <option key={e.id} value={e.number}>
                {e.number} — {e.name}
              </option>
            ))}
          </Select>
        </Row>
        <Row label="Mode" hint={chosen.hint}>
          <Select value={mode} onChange={(v) => setMode(v as typeof mode)}>
            {MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>
        </Row>
      </div>

      {start.error ? (
        <Alert>
          {start.error instanceof ApiError ? start.error.message : 'Could not start monitoring'}
        </Alert>
      ) : null}

      <div className="flex items-center gap-3">
        <Button disabled={!extension || start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? 'Calling you…' : 'Start'}
        </Button>
        {/*
          Said plainly. In most jurisdictions monitoring a call without disclosure is unlawful,
          and an admin panel that quietly offers the button without saying so is not doing its
          job. Every use is written to the operation log regardless.
        */}
        <p className="text-xs text-muted-foreground">
          Recorded in the operation log. Check what your local law requires you to disclose.
        </p>
      </div>
    </div>
  );
}

/** Live sessions, pinned above the list so an accidental barge is easy to find and stop. */
function MonitorBar({
  sessions,
  onChanged,
}: {
  sessions: MonitorSession[];
  onChanged: () => void;
}) {
  const setMode = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: string }) =>
      api.patch(`/api/v1/monitor/${id}`, { mode }),
    onSuccess: onChanged,
  });

  const stop = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/monitor/${id}`),
    onSuccess: onChanged,
  });

  return (
    <Card className="space-y-3 border-destructive/40">
      <p className="text-sm font-medium">You are monitoring {sessions.length} call(s)</p>
      {sessions.map((session) => (
        <div key={session.id} className="flex flex-wrap items-center gap-2">
          <Badge tone="warning">{session.mode}</Badge>

          {/* Escalation is one click, because coaching normally goes listen → whisper → barge
              and the moment to switch is while somebody is mid-sentence. */}
          {MODES.map(({ value, label, icon: Icon }) => (
            <Button
              key={value}
              variant="ghost"
              className={cn(session.mode === value && 'text-primary')}
              disabled={session.mode === value || setMode.isPending}
              onClick={() => setMode.mutate({ id: session.id, mode: value })}
            >
              <Icon className="mr-1.5 size-4" />
              {label}
            </Button>
          ))}

          <Button
            variant="ghost"
            className="ml-auto text-destructive"
            onClick={() => stop.mutate(session.id)}
          >
            <PhoneOff className="mr-1.5 size-4" />
            Stop
          </Button>
        </div>
      ))}
    </Card>
  );
}

function Elapsed({ since }: { since: string }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const total = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return (
    <span className="font-mono tabular-nums">
      {mm}:{ss}
    </span>
  );
}
